/**
 * Owner viewing slot form validation and datetime helpers.
 */

import {
  VIEWING_DURATION_OPTIONS,
  calculateViewingSlotTimes,
  findNextValidViewingDate,
  getViewingDateBlockMessage,
  isViewingDateBlocked,
} from './ownerViewingSlotDisplay';

export const DEFAULT_VIEWING_TIMEZONE = 'Europe/Berlin';

export function datetimeLocalToIso(localValue) {
  if (!localValue || !String(localValue).trim()) {
    return '';
  }
  const parsed = new Date(localValue);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  return parsed.toISOString();
}

export function formatSlotDateTime(isoValue) {
  if (!isoValue) {
    return '';
  }
  const parsed = new Date(isoValue);
  if (Number.isNaN(parsed.getTime())) {
    return String(isoValue);
  }
  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function validateOwnerViewingSlotForm(
  { date, startTime, durationMinutes, capacity, timezone },
  t = (key, fallback) => fallback || key,
  { schedulingConstraints } = {},
) {
  const errors = {};

  if (!date) {
    errors.date = t('owner.viewing_date_required', 'Date is required');
  } else if (schedulingConstraints) {
    const blocked = isViewingDateBlocked(date, schedulingConstraints);
    if (blocked.blocked) {
      const nextValidDate = findNextValidViewingDate({
        startDateStr: date,
        constraints: schedulingConstraints,
      });
      errors.date = getViewingDateBlockMessage(blocked.reason, t, { nextValidDate });
    }
  }
  if (!startTime) {
    errors.startTime = t('owner.viewing_start_required', 'Start time is required');
  }

  const duration = Number(durationMinutes);
  if (!Number.isFinite(duration) || !VIEWING_DURATION_OPTIONS.includes(duration)) {
    errors.durationMinutes = t('owner.viewing_duration_required', 'Duration is required');
  }

  const cap = Number(capacity);
  if (!Number.isFinite(cap) || cap < 1) {
    errors.capacity = t('owner.viewing_capacity_min', 'Capacity must be at least 1');
  }

  const times = calculateViewingSlotTimes({
    date,
    startTime,
    durationMinutes: duration,
  });

  if (date && startTime && duration && !times.endIso) {
    errors.startTime = t('owner.viewing_start_invalid', 'Start time is invalid');
  }

  const tz = (timezone || '').trim();
  if (!tz) {
    errors.timezone = t('owner.viewing_timezone_required', 'Timezone is required');
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
    payload: {
      start_time: times.startIso,
      end_time: times.endIso,
      capacity: Math.max(1, Math.floor(cap) || 1),
      timezone: tz || DEFAULT_VIEWING_TIMEZONE,
    },
  };
}

export function extractApiErrorMessage(error, fallback = 'Request failed') {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    if (typeof first === 'string') {
      return first;
    }
    if (first?.msg) {
      return String(first.msg);
    }
  }
  return fallback;
}

export function canOfferViewingSlotsForInquiryStatus(status) {
  const normalized = String(status || '').toLowerCase();
  return normalized === 'pending' || normalized === 'approved';
}
